/**
 * AudienceFields — shared component for cliente-ideal summary + AI generation.
 *
 * Generation calls `llm-proxy /chat` with `prompt_config_id=persona.generation.system`.
 * Caller persists summary into `business_context.persona.summary` (jsonb).
 */

import { useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { Sparkle as Sparkles } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toastError } from "@/lib/userMessages";
import {
  isNoConfiguredProviderResponse,
  NoConfiguredProviderError,
  isNoConfiguredProviderError,
} from "@/lib/edgeFnError";

export const AUDIENCE_MIN_CHARS = 120;

export interface AudienceFieldsProps {
  value: string;
  onChange: (next: string) => void;
  onGenerated?: (text: string) => void;
}

export function AudienceFields({ value, onChange, onGenerated }: AudienceFieldsProps) {
  const { session } = useSession();
  const [isGenerating, setIsGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenError(null);
    try {
      const token = await session?.getToken();
      if (!token) throw new Error("Usuário não autenticado.");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const res = await fetch(`${supabaseUrl}/functions/v1/llm-proxy/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          task: "persona",
          prompt_config_id: "persona.generation.system",
          user_message: "Com base no meu contexto profissional, gere o resumo do meu cliente ideal.",
        }),
      });

      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
          code?: string;
        };
        if (isNoConfiguredProviderResponse(res.status, err)) {
          throw new NoConfiguredProviderError(err.detail ?? err.error ?? "no_configured_provider", {
            status: res.status,
            code: err.error,
          });
        }
        throw new Error(err.error ?? `Erro ${res.status}`);
      }

      const data = (await res.json()) as { result?: { script?: string } };
      const text = data.result?.script?.trim();
      if (!text) throw new Error("Resposta vazia da IA.");

      // Only call onGenerated — it is responsible for updating both UI state
      // and persisting immediately. Calling onChange here would also trigger
      // the debounced save in the parent, causing a double mutation for the
      // same `persona` key (race condition).
      if (onGenerated) {
        onGenerated(text);
      } else {
        onChange(text);
      }
    } catch (e) {
      // BYOK: surface the CTA toast AND keep an inline label so users see
      // the requirement next to the action button.
      if (isNoConfiguredProviderError(e)) {
        toastError(e);
        setGenError("Conecte uma chave de IA em Perfil > Conexões para gerar.");
      } else {
        setGenError(e instanceof Error ? e.message : "Erro ao gerar.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const len = value.trim().length;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor="audience-summary">Resumo do cliente ideal</Label>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 text-xs"
          onClick={handleGenerate}
          disabled={isGenerating}
        >
          <Sparkles className={`h-3.5 w-3.5 ${isGenerating ? "animate-pulse" : ""}`} />
          {isGenerating ? "Gerando…" : "Gerar com IA"}
        </Button>
      </div>
      <div className="relative">
        <Textarea
          id="audience-summary"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Quem ele é, o que faz, o que sente antes de te procurar, o que ele quer alcançar."
          className="min-h-72 resize-y"
          disabled={isGenerating}
        />
        {isGenerating ? (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-md bg-background/70 backdrop-blur-sm"
            aria-live="polite"
          >
            <div className="relative flex h-12 w-12 items-center justify-center">
              <span className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
              <span className="absolute inset-2 animate-pulse rounded-full bg-primary/30" />
              <Sparkles className="relative h-6 w-6 text-primary animate-spin [animation-duration:2s]" />
            </div>
            <p className="text-sm text-muted-foreground">
              Gerando seu cliente ideal com IA…
            </p>
            <p className="text-xs text-muted-foreground/70">
              Isso pode levar alguns segundos.
            </p>
          </div>
        ) : null}
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {genError ? (
            <span className="text-destructive">{genError}</span>
          ) : (
            "Idade, profissão, dor principal, desejo, objeção principal."
          )}
        </span>
        <span className={len >= AUDIENCE_MIN_CHARS ? "text-emerald-500" : "text-muted-foreground"}>
          {len}/{AUDIENCE_MIN_CHARS}
        </span>
      </div>
    </div>
  );
}
